import React, { useEffect, useState } from "react";
import { KeyRound, Copy, Ban } from "lucide-react";
import { toast } from "sonner";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api } from "@/lib/api";
import { formatError } from "@/lib/auth";

const copy = (code) => navigator.clipboard.writeText(code).then(() => toast.success("Code copied"), () => toast.error("Copy failed"));

function CodeRow({ c, onRevoke }) {
  const dead = c.revoked || c.expired || c.uses >= c.max_uses;
  return (
    <div className={`py-2 border-b border-border last:border-0 flex items-center gap-2 flex-wrap text-xs ${dead ? "opacity-50" : ""}`} data-testid={`beta-code-row-${c.id}`}>
      <code className="font-mono text-sm text-foreground select-all" data-testid={`beta-code-value-${c.id}`}>{c.code}</code>
      {c.label && <span className="text-muted-foreground">{c.label}</span>}
      <Badge variant="outline">{c.role}</Badge>
      <span className="text-muted-foreground">{c.workgroup_names.join(", ")}</span>
      <Badge variant="outline" data-testid={`beta-code-uses-${c.id}`}>{c.uses}/{c.max_uses} used</Badge>
      <span className="text-muted-foreground">
        {c.revoked ? "revoked" : c.expired ? "expired" : c.expires_at ? `expires ${new Date(c.expires_at).toLocaleDateString()}` : "no expiry"}
      </span>
      {!dead && (
        <span className="ml-auto flex gap-1.5">
          <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => copy(c.code)} data-testid={`beta-code-copy-${c.id}`}><Copy className="w-3 h-3 mr-1" /> Copy</Button>
          <Button size="sm" variant="outline" className="h-7 text-xs text-rose-300" onClick={() => onRevoke(c.id)} data-testid={`beta-code-revoke-${c.id}`}><Ban className="w-3 h-3 mr-1" /> Revoke</Button>
        </span>
      )}
    </div>
  );
}

/** Admin: beta access codes people type on the sign-up page (limited uses, optional expiry). */
export function BetaCodes({ groups }) {
  const [codes, setCodes] = useState([]);
  const [f, setF] = useState({ label: "", role: "playtester", max_uses: 25, days: 30, workgroup_ids: [] });
  const load = () => api.listBetaCodes().then(setCodes).catch((e) => toast.error(formatError(e)));
  useEffect(() => { load(); }, []);
  const create = async () => {
    try {
      const c = await api.createBetaCode({ ...f, max_uses: Number(f.max_uses), days: Number(f.days) });
      await load();
      copy(c.code);
    } catch (e) {
      toast.error(formatError(e));
    }
  };
  const revoke = async (id) => { await api.revokeBetaCode(id).catch((e) => toast.error(formatError(e))); load(); };
  const toggle = (id, on) => setF({ ...f, workgroup_ids: on ? [...f.workgroup_ids, id] : f.workgroup_ids.filter((x) => x !== id) });
  return (
    <Card>
      <CardContent className="p-4 space-y-3" data-testid="beta-codes">
        <h3 className="font-semibold flex items-center gap-2"><KeyRound className="w-4 h-4 text-cyan-400" /> Beta access codes</h3>
        <p className="text-xs text-muted-foreground">People enter a code on the sign-in page ("Have a beta code? Sign up") and get an account straight away with the role and workgroups you pick here.</p>
        <div className="flex flex-wrap items-center gap-2">
          <Input value={f.label} onChange={(e) => setF({ ...f, label: e.target.value })} placeholder="Label (e.g. Discord beta wave 1)" className="h-8 text-xs w-56" data-testid="beta-code-label-input" />
          <Select value={f.role} onValueChange={(role) => setF({ ...f, role })}>
            <SelectTrigger className="h-8 w-32 text-xs" data-testid="beta-code-role-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="playtester">Playtester</SelectItem>
              <SelectItem value="admin">Admin</SelectItem>
            </SelectContent>
          </Select>
          <label className="text-xs text-muted-foreground flex items-center gap-1">Uses
            <Input type="number" min={1} max={10000} value={f.max_uses} onChange={(e) => setF({ ...f, max_uses: e.target.value })} className="h-8 w-20 text-xs" data-testid="beta-code-uses-input" />
          </label>
          <label className="text-xs text-muted-foreground flex items-center gap-1">Days valid (0 = no expiry)
            <Input type="number" min={0} max={365} value={f.days} onChange={(e) => setF({ ...f, days: e.target.value })} className="h-8 w-20 text-xs" data-testid="beta-code-days-input" />
          </label>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          {groups.map((g) => (
            <label key={g.id} className="flex items-center gap-1.5 text-xs">
              <Checkbox checked={f.workgroup_ids.includes(g.id)} onCheckedChange={(v) => toggle(g.id, v)} data-testid={`beta-code-group-${g.id}`} />
              {g.name}
            </label>
          ))}
          <Button size="sm" onClick={create} disabled={!f.workgroup_ids.length} data-testid="beta-code-create-btn">Create code</Button>
        </div>
        {codes.map((c) => <CodeRow key={c.id} c={c} onRevoke={revoke} />)}
      </CardContent>
    </Card>
  );
}
